import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { SidebarGroup } from 'src/app/shared/models/sidebarGroup';
import { environment } from 'src/environments/environment';

/**
 * Título da aba do navegador (#456): "<página> | <ambiente>". Fora do sistema (login, cadastro,
 * senha, páginas de erro) a página é "ConnectSea". Produção não tem sufixo de ambiente.
 */
export function buildAppTitle(
  pageName: string | undefined,
  environmentLabel: string,
): string {
  const base = pageName || 'ConnectSea';
  return environmentLabel ? `${base} | ${environmentLabel}` : base;
}

/** Singular do nome do menu, para "New PDA" e "Template #12": "ies" vira "y" e sai o "s" final. */
export function singularize(name: string): string {
  if (/ies$/i.test(name)) return name.replace(/ies$/i, 'y');
  if (/[^s]s$/i.test(name)) return name.slice(0, -1);
  return name;
}

/**
 * Nome da página pelo menu da sidebar: o item cuja rota é o maior prefixo da URL. Assim
 * /da/funds é "DA Funds" (e não "DAs", de /da), sem precisar cadastrar título rota por rota.
 * Logo depois da rota do item, "add" ou "new" é criação ("New PDA") e "edit/<id>" ou só o id
 * é edição ("PDA #1520"). Qualquer outro trecho (ex.: calls/review) fica com o nome do item.
 */
export function findMenuPageName(
  url: string,
  menuGroups: SidebarGroup[] | null | undefined,
): string | undefined {
  const path = url.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  let best: { name: string; route: string } | undefined;

  for (const group of menuGroups ?? []) {
    for (const item of group.items ?? []) {
      const route = item.route?.replace(/\/+$/, '');
      if (!route) continue;
      const matches = path === route || path.startsWith(`${route}/`);
      if (matches && (!best || route.length > best.route.length)) {
        best = { name: item.name, route };
      }
    }
  }
  if (!best) return undefined;

  const [first, second] = path.slice(best.route.length).split('/').filter(Boolean);
  if (first === 'add' || first === 'new') return `New ${singularize(best.name)}`;
  if (first === 'edit' && second) return `${singularize(best.name)} #${second}`;
  if (first && /^\d+$/.test(first)) return `${singularize(best.name)} #${first}`;
  return best.name;
}

/**
 * Junta a navegação (URL e `title` da rota, vindos do AppTitleStrategy) com o menu da sidebar
 * que está na tela e atualiza o título. Recalcula a cada mudança de qualquer um dos dois, então
 * não importa se a sidebar abre antes ou depois do fim da navegação.
 */
@Injectable({ providedIn: 'root' })
export class PageTitleService {
  private url = '';
  private routeTitle: string | undefined;
  private menuGroups: SidebarGroup[] | null = null;

  constructor(private readonly title: Title) {}

  setNavigation(url: string, routeTitle: string | undefined): void {
    this.url = url;
    this.routeTitle = routeTitle;
    this.update();
  }

  setMenu(menuGroups: SidebarGroup[]): void {
    this.menuGroups = menuGroups;
    this.update();
  }

  /** Só limpa se ainda for o mesmo menu: a sidebar do módulo novo pode ter aberto antes. */
  clearMenu(menuGroups: SidebarGroup[]): void {
    if (this.menuGroups !== menuGroups) return;
    this.menuGroups = null;
    this.update();
  }

  private update(): void {
    const pageName =
      findMenuPageName(this.url, this.menuGroups) ?? this.routeTitle;
    this.title.setTitle(buildAppTitle(pageName, environment.environmentLabel));
  }
}
